import React, { useEffect, useLayoutEffect, useState } from 'react';
import useCommentsMessageHub, {
    CommentMessage,
    IOwnedMessageHubHook,
    QueuedUpdate,
} from '@components-clean/core/comments-side-bar/comments-message-hub';

// Messages from the RiskIQ hub also say which risk / CtQ the comment belongs to
export interface RiskIqCommentMessage extends CommentMessage {
    parentId: string;
    entityType: 'risk' | 'ctq';
}

export interface RiskIqEntityIds {
    riskIds: string[];
    ctqIds: string[];
}

interface RiskIqCommentsMessageHubProps {
    protocolDocumentId: string;
    // Called once the message hub has connected (or after a grace period), so no updates are missed. The message hub is
    // passed so polls can clear its queue, and items can be queued on it to be refetched
    loadCommentCounts: (messageHub: IOwnedMessageHubHook<RiskIqCommentMessage>) => void;
    commentCountsLoaded: boolean;
    onCommentsUpdated: (entityIds: RiskIqEntityIds) => Promise<boolean>;
    renderCommentsSideBar: (messageHub: IOwnedMessageHubHook<RiskIqCommentMessage>) => React.ReactNode;
}

// The counts are of all comments, so only adding or deleting one changes them. Deletes have no update type
const changesCommentCount = (message: RiskIqCommentMessage) =>
    message.updateType === 'CommentAdded' || message.updateType === null;

const toRiskIqEntityIds = (messages: QueuedUpdate<RiskIqCommentMessage>[]): RiskIqEntityIds => ({
    riskIds: messages.filter((message) => message.entityType === 'risk').map((message) => message.parentId),
    ctqIds: messages.filter((message) => message.entityType === 'ctq').map((message) => message.parentId),
});

// Owns the connection to the RiskIQ comments hub, grouped by protocol document, and passes it to renderCommentsSideBar so the
// comments sidebar can share it. Must be keyed by protocolDocumentId as the message hub can't change group once mounted
const RiskIqCommentsMessageHub = ({
    protocolDocumentId,
    loadCommentCounts,
    commentCountsLoaded,
    onCommentsUpdated,
    renderCommentsSideBar,
}: RiskIqCommentsMessageHubProps) => {
    const [waitingOnMessageHubConnecting, setWaitingOnMessageHubConnecting] = useState(true);

    const messageHub = useCommentsMessageHub<RiskIqCommentMessage>({
        messageHubRelativeEndpoint: 'risk-iq-hub',
        groupName: protocolDocumentId,
        // A delete changes the counts the same as an update, so both are queued until the counts have loaded
        queueDeletes: true,
        // Dropped before they're queued, so a later edit can't replace an added comment still waiting in the queue
        messageFilter: changesCommentCount,
    });

    useLayoutEffect(() => {
        messageHub.subscribe({
            onCommentsUpdated: (messages) => onCommentsUpdated(toRiskIqEntityIds(messages)),
        });
    }, []);

    useEffect(() => {
        const messageHubConnectingTimeout = setTimeout(() => setWaitingOnMessageHubConnecting(false), 1000);
        return () => {
            clearTimeout(messageHubConnectingTimeout);
        };
    }, []);

    useEffect(() => {
        if (messageHub.connectedAtLeastOnce) {
            setWaitingOnMessageHubConnecting(false);
        }
    }, [messageHub.connectedAtLeastOnce]);

    useEffect(() => {
        if (!waitingOnMessageHubConnecting) {
            loadCommentCounts(messageHub);
        }
    }, [waitingOnMessageHubConnecting]);

    // Updates received while the counts were loading are queued, then applied once they've loaded
    useEffect(() => {
        if (commentCountsLoaded) {
            messageHub.startProcessingUpdates();
        }
    }, [commentCountsLoaded]);

    return renderCommentsSideBar(messageHub);
};

export default RiskIqCommentsMessageHub;
